import { Box, FormHelperText, Stack, Button, Grid, Card, CardContent, Checkbox, FormControl, FormControlLabel } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import { useRecordContext, useEditContext } from "ra-core";
import { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Edit from "~/controller/Edit";
import WithSave from "~/controller/WithSave";
import { LegalSettingDto } from "~/gen/api/securefile";



const legalWarningResource = 'settings-clients-securefile-legal-warning-config'

const LegalWarningSettings: FC = () => {
  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")
  
  return (
    <Edit resource={legalWarningResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
      <LegalWarningSettingsBody />
    </Edit>
  )
}


interface LSetting {
  showLegalWarning: boolean;
  decryptionLegalWarning: string;
  submit: any,
}

const initialVal = (record: LegalSettingDto | undefined) => ({
  settings: {
    showLegalWarning: record?.showLegalWarning ?? false,
    decryptionLegalWarning: record?.decryptionLegalWarning ?? '',
  },
  touched: null as {[k in keyof LSetting]+?: boolean}|null,
  errors: null as {[k in keyof LSetting]+?: string}|null,
})

const LegalWarningSettingsBody: FC = () => {
  const record : LegalSettingDto|undefined = useRecordContext() as any
  const { loaded } = useEditContext()
  const [states, setStates] = useState(initialVal(record))
  useEffect(() => {
    if (record) {
      setStates(initialVal(record))
    }
  }, [record])

  const validate = (s1: typeof states) => {
    const errors: any[] = []
    if (s1.settings.showLegalWarning && !s1.settings.decryptionLegalWarning) {
      errors.push({ decryptionLegalWarning: 'Legal Warning text is required' })
    } 
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }

  const history = useHistory()
  useEffect(() => {
    const unregister = history.block((newLocation, a) => {
      let pass = !Boolean(states.touched)
      if (!pass) {
        // discard edits will restore 'legal warning', in turn will trigger editor to set 'touched'
        if (Object.keys(states.touched ?? {}).length === 1 
        && states.touched?.decryptionLegalWarning 
        && states.settings.decryptionLegalWarning === record?.decryptionLegalWarning) {
          pass = true
        }
      }
      return a === 'PUSH' && !pass ? 'There are unsaved edits, discard edits and continue?' : undefined
    })
    return () => {
      unregister()
    }
  }, [states, record, history])

  return (
    <>
      {states.errors?.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {states.errors?.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ pb: 2 }} >
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => { setStates(initialVal(record)) }}
        >
          Discard
        </Button>
        <WithSave 
          render={({ save, saving }) =>
            <Button
              color="primary"
              disabled={!states.touched || saving}
              type="submit"
              variant="contained"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setStates(v => {
                  const v1 = {...v,
                    touched: {
                      showLegalWarning: true,
                      decryptionLegalWarning: true,
                    },
                  }
                  v1.errors = validate(v1)
                  if (!v1.errors) {  
                    setTimeout(() => {
                      save({...v1.settings}, {
                        onSuccess: () => {
                          setStates(v2 => ({...v2, touched: null}))
                        },
                        onFailure: (err:any) => {
                          setStates(v2 => ({...v2, errors: { submit: err.message }}))
                        },
                      })
                    })
                  }
                  return v1
                })
                return false
              }}
            >
              Save
            </Button>
          }
        />
      </Stack>
      
      <Card>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl>
                <FormControlLabel
                  label="Show Legal Warning on SecureFile App"
                  control={
                    <Checkbox checked={states.settings.showLegalWarning} 
                      onChange={(e) => setStates(v => ({...v,
                        touched: {...v.touched, showLegalWarning: true,
                          ...(!e.target.checked && {decryptionLegalWarning:false})
                        },
                        settings: {...v.settings, showLegalWarning: e.target.checked}
                      }))}
                    />
                  }
                />
              </FormControl>
            </Grid>

            {loaded &&
              <Grid item xs={12}>
                <Box sx={{ 
                  visibility: (states.settings.showLegalWarning || Boolean(states.settings.decryptionLegalWarning))
                    ? 'visible' : 'hidden',
                  display: (states.settings.showLegalWarning || Boolean(states.settings.decryptionLegalWarning))
                    ? 'block' : 'none'
                }}>
                  <Editor 
                    tinymceScriptSrc="/tinymce/tinymce.min.js"
                    init={{
                      branding: false,
                      promotion: false,
                      plugins: 'pagebreak lists wordcount',
                      menubar: false,
                      height: 530,
                      toolbar: "undo redo styles fontfamily fontsize | bold italic underline strikethrough hr | align numlist bullist | lineheight outdent indent| forecolor backcolor removeformat pagebreak",
                    }}
                    disabled={!states.settings.showLegalWarning}
                    initialValue={record?.decryptionLegalWarning ?? ''}
                    value={states.settings.decryptionLegalWarning}
                    onEditorChange={(value, editor) => {
                      setStates(v => {
                        const v1 = ({...v,
                          touched: {...v.touched, decryptionLegalWarning: true},
                          settings: {...v.settings, 
                            decryptionLegalWarning: value
                          }
                        })
                        v1.errors = validate(v1)
                        return v1
                      })
                    }}
                  />
                </Box>
                {Boolean(states.errors?.decryptionLegalWarning && states.touched?.decryptionLegalWarning) &&
                  <FormHelperText error sx={{ ml: 2 }}>{states.errors?.decryptionLegalWarning}</FormHelperText>}
              </Grid>
            }
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}

export default LegalWarningSettings;
